import type { NodePropertySchema } from '../../../types/node-properties';

const schema: NodePropertySchema = {
  nodeType: 'asana-webhook-trigger',
  version: '1.0.0',
  properties: [
    // === Authentication ===
    {
      key: 'credentialId',
      label: 'Asana Credential',
      type: 'credential',
      required: true,
      description: 'Select the Asana OAuth2 credential to use',
      typeOptions: {
        credentialType: 'asana_oauth2',
      },
    },

    // === Workspace selector (modeToggle) ===
    {
      key: 'workspaceGid',
      label: 'Workspace',
      type: 'string',
      required: true,
      description: 'The workspace containing the resource to subscribe to',
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getWorkspaces',
          nodeType: 'asana',
          cacheResults: true,
          cacheTtl: 60000,
        },
        textModeLabel: 'Expression',
      },
    },

    // === Resource type to watch ===
    {
      key: 'resourceType',
      label: 'Watch',
      type: 'options',
      default: 'project',
      required: true,
      description: 'The type of Asana resource to subscribe to webhook events for',
      options: [
        { name: 'Project', value: 'project', description: 'Subscribe to events on a project (tasks added, changed, etc.)' },
        { name: 'Task', value: 'task', description: 'Subscribe to events on a specific task (comments, changes, etc.)' },
      ],
    },

    // === Project selector (modeToggle, shown when watching a project) ===
    {
      key: 'resourceGid',
      label: 'Project',
      type: 'string',
      required: true,
      description: 'The project to subscribe to webhook events for',
      displayOptions: {
        show: { resourceType: ['project'] },
      },
      modeToggle: {
        enabled: true,
        defaultMode: 'data',
        loadOptions: {
          method: 'getProjects',
          nodeType: 'asana',
          dependsOn: ['workspaceGid'],
          cacheResults: true,
          cacheTtl: 30000,
        },
        textModeLabel: 'Expression',
      },
    },

    // === Task GID (text input, shown when watching a task) ===
    {
      key: 'resourceGid',
      label: 'Task GID',
      type: 'string',
      required: true,
      description: 'The GID of the Asana task to subscribe to webhook events for. You can find this in the task URL.',
      placeholder: 'e.g. 1234567890123456',
      displayOptions: {
        show: { resourceType: ['task'] },
      },
    },

    // === Event Filters ===
    {
      key: 'filterAction',
      label: 'Event Action Filter',
      type: 'options',
      default: 'all',
      description: 'Filter events by action type',
      options: [
        { name: 'All Actions', value: 'all' },
        { name: 'Changed', value: 'changed' },
        { name: 'Added', value: 'added' },
        { name: 'Removed', value: 'removed' },
        { name: 'Deleted', value: 'deleted' },
        { name: 'Undeleted', value: 'undeleted' },
      ],
    },
    {
      key: 'filterResourceType',
      label: 'Resource Type Filter',
      type: 'options',
      default: 'all',
      description: 'Filter events by resource type',
      options: [
        { name: 'All Types', value: 'all' },
        { name: 'Task', value: 'task' },
        { name: 'Story', value: 'story' },
        { name: 'Project', value: 'project' },
        { name: 'Section', value: 'section' },
      ],
    },
  ],
};

export default schema;
